/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useOpenCheckInEvents, checkInApi } from '../../../hooks/useCheckInDesk';
import { groupEvents, defaultEventId } from './checkInResults';
import EventPicker from './EventPicker';
import { idOf } from './checkInRules';
import GenerateCertificateDialog from './GenerateCertificateDialog';
import {
  STATES, buildParticipantRows, coursesByActivity, countStates, fetchEventCertificateData, filterRows,
} from './certificatesTab';

const CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'issued', label: 'Issued' },
  { id: 'not_checked_in', label: 'Not checked in' },
  { id: 'no_template', label: 'No template' },
];

const PASS_STATUS = {
  CHECKED_IN: { label: 'Checked in', tone: 'in' },
  CHECKED_OUT: { label: 'Checked out', tone: 'out' },
  ISSUED: { label: 'Not arrived', tone: 'none' },
};

/**
 * Participants → Certificates. Every participant of the picked event with
 * their check-in and certificate state; Generate opens the dialog that
 * confirms their details and issues the certificate.
 */
export default function CertificatesView() {
  const desk = useOpenCheckInEvents();
  const groups = useMemo(() => groupEvents(desk.events, desk.now), [desk.events, desk.now]);
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState('all');
  const [activity, setActivity] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null); // the row whose dialog is open

  // The event lives in the URL like the tab, and only one of this organizer's own.
  const asked = Number(params.get('event')) || null;
  const eventId = desk.events.some((row) => row.id === asked) ? asked : defaultEventId(groups);
  const event = desk.events.find((row) => row.id === eventId) || null;

  const data = useQuery({
    queryKey: ['certificatesTab', eventId],
    queryFn: () => fetchEventCertificateData(checkInApi, eventId),
    enabled: eventId != null,
    staleTime: 15_000,
  });

  const rows = useMemo(() => (data.data ? buildParticipantRows(data.data) : []), [data.data]);
  const activities = useMemo(
    () => (data.data?.activities || []).map((a) => ({ id: String(idOf(a.id)), name: String(a.name || '') })),
    [data.data],
  );
  const courses = useMemo(
    () => coursesByActivity(data.data?.routes, data.data?.venues, activities.map((a) => a.id)),
    [data.data, activities],
  );
  const scoped = activity === 'all' ? rows : rows.filter((row) => String(row.activityId) === activity);
  const counts = countStates(scoped);
  const shown = filterRows(scoped, state, search);
  const withoutTemplate = activities.filter((a) => !(data.data?.choices || []).some((c) => String(idOf(c.event_activity_id)) === a.id));

  if (desk.isLoading) return <div className="org-content"><div className="org-empty"><p>Loading your events…</p></div></div>;
  if (eventId == null) {
    return (
      <div className="org-content">
        <div className="org-empty"><h2>No events yet</h2><p>Certificates appear here once you have an event with participants.</p></div>
      </div>
    );
  }

  return (
    <div className="org-content ochh ocrt">
      <div className="ochh-bar">
        <EventPicker
          groups={groups}
          selected={event}
          onSelect={(id) => {
            setParams({ view: 'certificates', event: String(id) }, { replace: true });
            setState('all'); setActivity('all'); setSearch('');
          }}
        />
        {activities.length > 1 && (
          <select className="orgf-select ocrt-activity" value={activity} onChange={(e) => setActivity(e.target.value)} aria-label="Activity">
            <option value="all">All activities</option>
            {activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <button type="button" className="ochk-iconbtn" onClick={() => data.refetch()} aria-label="Refresh" disabled={data.isFetching}>
          <i className={`bi bi-arrow-clockwise${data.isFetching ? ' ochk-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {data.isError && (
        <div className="org-empty" role="alert">
          <h2>Couldn&rsquo;t load the certificates</h2>
          <p>If certificates were never set up for this app, the certificate tables may be missing.</p>
          <button type="button" className="org-btn" style={{ marginTop: 14 }} onClick={() => data.refetch()}>Retry</button>
        </div>
      )}
      {data.isLoading && <div className="org-empty"><p>Loading participants…</p></div>}

      {data.isSuccess && (
        <>
          <div className="ochh-tiles ocrt-tiles">
            <div className="ochh-tile is-in">
              <span className="org-lab">Checked in</span>
              <b className="org-num">{counts.ready + counts.issued + counts.no_template}</b>
              <span>of {counts.all} registered</span>
            </div>
            <div className="ochh-tile ocrt-tile-issued">
              <span className="org-lab">Certificates issued</span>
              <b className="org-num">{counts.issued}</b>
              <span>{counts.all ? Math.round((counts.issued / counts.all) * 100) : 0}% of participants</span>
            </div>
            <div className="ochh-tile ocrt-tile-ready">
              <span className="org-lab">Ready to generate</span>
              <b className="org-num">{counts.ready}</b>
              <span>checked in, no certificate yet</span>
            </div>
            <div className="ochh-tile is-none">
              <span className="org-lab">Not checked in</span>
              <b className="org-num">{counts.not_checked_in}</b>
              <span>can&rsquo;t get a certificate yet</span>
            </div>
          </div>

          {withoutTemplate.length > 0 && (
            <div className="ocrt-banner" role="status">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />
              <span>
                <b>{withoutTemplate.map((a) => a.name).join(', ')}</b>
                {withoutTemplate.length === 1 ? ' has' : ' have'} no certificate template, so {withoutTemplate.length === 1 ? 'its' : 'their'} participants can&rsquo;t be generated.
              </span>
              <Link className="org-btn ghost ocrt-small" to={`/organizer/events/${eventId}/edit`}>Set templates</Link>
            </div>
          )}

          <section className="ochh-people" aria-label="Participants">
            <div className="ochh-peoplehead">
              <h3>Certificates</h3>
              <p>Generate a certificate for each participant who checked in. The participant gets an email with a link to download it.</p>
            </div>
            <div className="ochh-peoplebar">
              <div className="ochk-filters" role="group" aria-label="Filter participants">
                {CHIPS.map((chip) => (
                  <button key={chip.id} type="button" className="ochk-filter" aria-pressed={state === chip.id} onClick={() => setState(chip.id)}>
                    {chip.label}<span>{counts[chip.id]}</span>
                  </button>
                ))}
              </div>
              <label className="sr-only-org" htmlFor="ocrt-search">Search participants</label>
              <input
                id="ocrt-search" className="orgf-input ochh-peoplesearch" type="search"
                placeholder="Search name, email or passcode" value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {shown.length === 0 && (
              <p className="ochk-recentmsg">{search ? 'Nobody matches that search.' : 'Nobody here.'}</p>
            )}
            {shown.length > 0 && (
              <table className="ochh-table ocrt-table">
                <thead>
                  <tr>
                    <th scope="col">Participant</th>
                    <th scope="col">Activity</th>
                    <th scope="col">Check-in</th>
                    <th scope="col">Certificate</th>
                    <th scope="col"><span className="sr-only-org">Action</span></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => {
                    const pass = PASS_STATUS[row.status] || PASS_STATUS.ISSUED;
                    const cert = STATES[row.state];
                    return (
                      <tr key={row.passId}>
                        <td data-label="Participant">
                          <b className="ochk-name">{row.name || row.passcode || '—'}</b>
                          <span className="ocrt-meta">
                            {row.passcode && <span className="ochk-code">{row.passcode}</span>}
                            {row.email && <span>{row.email}</span>}
                          </span>
                        </td>
                        <td data-label="Activity">{row.activityName || '—'}</td>
                        <td data-label="Check-in"><span className={`ochh-status is-${pass.tone}`}>{pass.label}</span></td>
                        <td data-label="Certificate">
                          <span className={`ocrt-chip is-${cert.tone}`}>{cert.label}</span>
                          {row.certificate?.certificate_no && <span className="ochk-code ocrt-no">{row.certificate.certificate_no}</span>}
                        </td>
                        <td data-label="Action" className="ocrt-actioncell">
                          {row.state === 'issued' ? (
                            <span className="ocrt-rowactions">
                              <button type="button" className="org-btn ghost ocrt-small" onClick={() => setOpen({ row, mode: 'view' })}>
                                <i className="bi bi-eye" aria-hidden="true" /> View
                              </button>
                              <button type="button" className="org-btn ghost ocrt-small" onClick={() => setOpen({ row, mode: 'regenerate' })}>
                                <i className="bi bi-arrow-repeat" aria-hidden="true" /> Regenerate
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={`org-btn ocrt-small${row.state === 'ready' ? '' : ' ghost'}`}
                              onClick={() => setOpen({ row, mode: 'generate' })}
                            >
                              <i className="bi bi-award" aria-hidden="true" /> Generate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {open && event && (
        <GenerateCertificateDialog
          key={`${open.row.passId}-${open.mode}`}
          row={open.row}
          mode={open.mode}
          event={event}
          course={courses[String(open.row.activityId)]}
          onClose={() => setOpen(null)}
          onGenerated={() => data.refetch()}
        />
      )}
    </div>
  );
}
